import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, describeAuthError, formatApiError } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import LanguageToggle from "@/components/LanguageToggle";
import { ShieldCheck, Lock, EnvelopeSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMEMBER_KEY = "lgmg_remember_email";

export default function Login() {
  const { user, login, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || "");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Recuperar contraseña
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  // Estado inicial desde caché para evitar el "flash" del logo al cargar.
  // null = aún no sabemos (no se pinta el logo hasta confirmar).
  const [logoEnabled, setLogoEnabled] = useState(() => {
    const c = localStorage.getItem("login_logo_enabled");
    return c === null ? null : c === "true";
  });
  const [locked, setLocked] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(() => {
    const c = localStorage.getItem("signup_enabled");
    return c === null ? true : c === "true";
  });

  useEffect(() => {
    axios.get(`${API}/system/login-branding`)
      .then(r => {
        const val = r.data?.login_logo_enabled !== false;
        setLogoEnabled(val);
        localStorage.setItem("login_logo_enabled", String(val));
        const su = r.data?.signup_enabled !== false;
        setSignupEnabled(su);
        localStorage.setItem("signup_enabled", String(su));
      })
      .catch(() => {});
  }, []);

  // Where to go after login (used by QR-scan → login → verify flow)
  const rawRedirect = new URLSearchParams(location.search).get("redirect") || "";
  const redirectTo = rawRedirect.startsWith("/") ? rawRedirect : "/";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-white">{t('common.loading')}</div>
      </div>
    );
  }
  // Si ya está autenticado y su suscripción NO está bloqueada, entra a la app.
  // Si está bloqueado por suscripción, se muestra el formulario para poder
  // iniciar sesión con otra cuenta (p. ej. Super Admin) sin quedar atrapado.
  const subBlocked = user?.subscription?.required && !user?.subscription?.allowed;
  if (user && !subBlocked) return <Navigate to={redirectTo} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLocked(false);
    setSubmitting(true);
    try {
      await login(email, password, "operational");
      if (remember) localStorage.setItem(REMEMBER_KEY, email);
      else localStorage.removeItem(REMEMBER_KEY);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(describeAuthError(err));
      if (err?.response?.status === 429) setLocked(true);
    } finally {
      setSubmitting(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotSubmitting(true);
    setForgotMsg("");
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotMsg(data?.message || t('login.forgotSent', 'Si el correo está registrado, te enviaremos un enlace para restablecer tu contraseña.'));
    } catch (err) {
      setForgotMsg(formatApiError(err.response?.data?.detail) || t('login.forgotSent', 'Si el correo está registrado, te enviaremos un enlace para restablecer tu contraseña.'));
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Diagonal red accent */}
      <div className="absolute top-0 left-0 w-full h-2 bg-[var(--brand,#E31E24)]" />
      <div className="absolute bottom-0 right-0 w-1/3 h-1 bg-[var(--brand,#E31E24)]" />
      {/* Language toggle + acceso a suscripción autoservicio */}
      <div className="absolute top-6 right-6 z-20 flex items-center gap-2">
        {signupEnabled && (
          <button
            type="button"
            onClick={() => navigate("/registro-suscripcion")}
            data-testid="login-subscription-btn"
            className="text-[11px] font-semibold text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 rounded-full px-3 py-1.5 transition-colors"
          >
            {t('login.subscribe', { defaultValue: 'Suscripción' })}
          </button>
        )}
        <LanguageToggle variant="dark" />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="mb-8 text-center">
          {logoEnabled && (
            <div className="inline-flex items-center justify-center -mb-6">
              <img src="/assets/logo.png" alt="HSE" className="w-40 h-40 object-contain" />
            </div>
          )}
          <h1 className="text-4xl font-black text-white tracking-tight mb-1">HSE</h1>
          <p className="text-slate-400 text-xs uppercase tracking-widest">{t('login.subtitle')}</p>
        </div>

        <Card className="border-slate-800 bg-zinc-950 text-white shadow-2xl">
          <CardContent className="p-8">
            <h2 className="text-xl font-bold mb-6 border-l-4 border-[var(--brand,#E31E24)] pl-3">{t('login.title')}</h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-wider">{t('login.email')}</Label>
                <div className="relative">
                  <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="login-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    required
                    className="pl-10 bg-black border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-wider">{t('login.password')}</Label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="login-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 bg-black border-slate-700 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              {error && (
                <div data-testid="login-error" className="text-sm text-[var(--brand,#E31E24)] bg-[var(--brand,#E31E24)]/10 border border-[var(--brand,#E31E24)]/30 px-3 py-2 rounded-sm">
                  {error}
                  {locked && (
                    <button
                      type="button"
                      data-testid="login-recover-btn"
                      onClick={() => { setForgotEmail(email); setForgotMsg(""); setForgotOpen(true); }}
                      className="mt-2 w-full text-center bg-[var(--brand,#E31E24)] hover:bg-[var(--brand-dark,#c01a1f)] text-white text-xs font-bold uppercase tracking-wider py-2 rounded-sm transition-colors"
                    >
                      {t('login.recover', 'Recuperar contraseña')}
                    </button>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="login-remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 accent-[var(--brand,#E31E24)] bg-black border-slate-700"
                  />
                  {t('login.remember', 'Recordarme')}
                </label>
                <button
                  type="button"
                  data-testid="login-forgot-link"
                  onClick={() => { setForgotEmail(email); setForgotMsg(""); setForgotOpen(true); }}
                  className="text-xs text-slate-400 hover:text-white underline underline-offset-2 transition-colors"
                >
                  {t('login.forgot', '¿Olvidaste tu contraseña?')}
                </button>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={submitting}
                className="w-full bg-[var(--brand,#E31E24)] hover:bg-[var(--brand-dark,#c01a1f)] text-white font-bold tracking-wide py-6 rounded-sm transition-colors"
              >
                {submitting ? `${t('common.loading')}` : t('login.signIn').toUpperCase()}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-800 text-xs text-slate-500 text-center">
              <p>{t('login.footer')}</p>
              <p className="mt-1 text-[10px]">{t('login.norms')}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={18} weight="duotone" /> {t('login.forgotTitle', 'Recuperar contraseña')}
            </DialogTitle>
            <p className="text-xs text-slate-500 mt-1">
              {t('login.forgotDesc', 'Escribe tu correo y te enviaremos un enlace para crear una nueva contraseña.')}
            </p>
          </DialogHeader>
          <form onSubmit={submitForgot} className="space-y-3">
            <div>
              <Label htmlFor="forgot-email">{t('login.email', 'Correo electrónico')}</Label>
              <Input
                id="forgot-email"
                type="email"
                required
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="tucorreo@empresa.com"
                data-testid="forgot-email-input"
              />
            </div>
            {forgotMsg && (
              <div data-testid="forgot-message" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded-sm">
                {forgotMsg}
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>
                {t('common.close', 'Cerrar')}
              </Button>
              <Button type="submit" disabled={forgotSubmitting}
                      className="bg-[var(--brand,#E31E24)] hover:bg-[var(--brand-dark,#c01a1f)]"
                      data-testid="forgot-submit">
                {forgotSubmitting ? t('common.loading', 'Cargando…') : t('login.forgotSend', 'Enviar enlace')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
